import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandVideoBackdrop } from './BrandVideoBackdrop';
import { setLanguage } from '../../lib/i18n';
import { setPreference } from '../../lib/preferences';
import { LANG_LIST, type LangCode } from '../../lib/i18n/languages';
import { haptic } from '../../lib/haptic';

type Props = {
  // Cross-language editorial italic anchor word. Stays in Latin script across
  // all 8 languages so the Fraunces italic flourish reads identically
  // everywhere. See feedback_cross_language_editorial_italic.md
  anchor: string;
  // The action verb that follows the anchor (e.g. "Login", "लॉगिन", "up").
  // This is the only part that translates between languages.
  actionVerb: string;
  subhead: string;
  children: ReactNode;
  // Optional footer slot for secondary links (e.g. "New driver? Sign up",
  // "Already have an account? Sign in"). Rendered inside the glass card
  // below the form so the link inherits the light-theme styling.
  footer?: ReactNode;
};

export function AuthScreenShell({ anchor, actionVerb, subhead, children, footer }: Props) {
  const { i18n } = useTranslation();
  const activeLang = (i18n.language?.split('-')[0] as LangCode) || 'en';

  const onPickLang = (code: LangCode) => {
    if (code === activeLang) return;
    haptic.tap();
    setLanguage(code);
    setPreference('language', code);
  };

  return (
    <>
      <BrandVideoBackdrop />
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255, 255, 255, 0.12), transparent 70%), linear-gradient(180deg, rgba(15, 59, 38, 0.10) 0%, rgba(15, 59, 38, 0.20) 100%)',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          minHeight: 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 16px))',
        }}
      >
        <div
          // Force light-mode tokens inside the glass card. The card's
          // rgba(255,255,255,0.78) background stays light in both themes,
          // so the text inside must stay dark too. data-theme="light" overrides
          // the inherited [data-theme="dark"] on <html> for this subtree.
          // See feedback_data_theme_subtree_override.md
          data-theme="light"
          style={{
            width: '100%',
            maxWidth: '400px',
            background: 'rgba(255, 255, 255, 0.78)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.55)',
            borderRadius: '20px',
            boxShadow: '0 24px 60px rgba(15, 59, 38, 0.14), 0 4px 12px rgba(15, 59, 38, 0.06)',
            padding: '32px 28px',
          }}
        >
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.025em',
              textAlign: 'center',
              color: 'var(--color-text)',
              margin: '0 0 6px',
            }}
          >
            <span className="serif-accent">{anchor}</span>{' '}
            {actionVerb}
          </h1>
          <p
            style={{
              fontSize: '15px',
              lineHeight: 1.5,
              textAlign: 'center',
              color: 'var(--color-text-secondary)',
              margin: '0 0 28px',
            }}
          >
            {subhead}
          </p>

          {children}

          {footer ? (
            <div
              style={{
                marginTop: '20px',
                textAlign: 'center',
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
              }}
            >
              {footer}
            </div>
          ) : null}
        </div>

        <div
          role="group"
          aria-label="Language"
          data-theme="light"
          style={{
            position: 'fixed',
            bottom: 'env(safe-area-inset-bottom, 16px)',
            // Pin to the same 440px mobile column the rest of the app
            // uses (driver-frame in AppShell). On mobile this collapses
            // to full-width; on desktop the pills no longer hug the
            // bottom-left corner of the viewport.
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 440,
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            padding: '12px 16px',
            zIndex: 3,
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {LANG_LIST.map((lang) => {
            const active = activeLang === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => onPickLang(lang.code)}
                aria-pressed={active}
                style={{
                  flexShrink: 0,
                  padding: '8px 14px',
                  borderRadius: 'var(--radius-pill)',
                  background: active ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(12px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                  border: active ? '1px solid rgba(46, 158, 101, 0.45)' : '1px solid rgba(255, 255, 255, 0.55)',
                  boxShadow: active ? '0 4px 14px rgba(15, 59, 38, 0.14)' : '0 2px 8px rgba(15, 59, 38, 0.08)',
                  fontSize: '14px',
                  fontWeight: active ? 700 : 500,
                  color: active ? 'var(--green-700)' : 'var(--color-text)',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {lang.native}
              </button>
            );
          })}
        </div>

        {/* Invisible reCAPTCHA container — Firebase mounts here on phone-OTP send.
            Harmless on screens that don't use Firebase; the element is empty
            until requestOtp() initialises the verifier. */}
        <div id="recaptcha-container" />
      </div>
    </>
  );
}
